// src/modules/app-views/dataSearch/pages/Document/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect, useMemo, useState } from 'react';
import { Tabs } from 'antd';
import { FileSyncOutlined } from '@ant-design/icons';
import { SimpleFormProvider } from 'components/SimpleComponents/SimpleFormProvider';
import SimpleFormProviderInitializer from 'components/SimpleComponents/SimpleFormProviderInitializer';
import SimpleLoadingArea from 'components/SimpleComponents/SimpleLoadingArea';
import { useDocumentContext } from 'components/core/DocumentProvider';
import DocumentIdentifier from 'components/core/DocumentIdentifier';
import { buildOriginIcon } from 'components/JarvislyComponents/DocumentInfoIcons';
import useZustand from 'hooks/useZustand';
import useFormInstance from 'hooks/useFormInstance';
import CardHeaderWithCountry from 'layouts/Pages/PageHeader/CardHeaderWithCountry';
import EntityRecordTab from 'modules/app-views/companies/pages/Document/tabs/RecordTab';
import EntityAdditionalInfoTab from 'modules/app-views/companies/pages/Document/tabs/AdditionalInfoTab';
import appService from 'services/appService';
import translateX from 'utils/translateX';
import { buildInitialValues } from 'utils/mock';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const IS_MOCK_STEP = false;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const DataSearchDocument = props => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { tab, initTab, handleTabChange, availableTabs, buildTabs, data, viewMode } =
    useDocumentContext();

  // Get module configuration to inherit entityType settings
  const storedCurrentPage = useZustand('module', 'currentPage');
  const storedUseApi = useZustand('global', 'useApi');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const { formRef, isInitializedRef } = useFormInstance({ data });

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const locale = appService.getLocale();
  const _id = storedUseApi?.urlInfo?.params?._id;

  // Get allowed entityTypes from module config
  const configFilter = useMemo(() => {
    if (storedCurrentPage?.pagination?.filter) {
      return storedCurrentPage.pagination.filter;
    }
    if (storedCurrentPage?.api?.query?.filter) {
      try {
        return JSON.parse(storedCurrentPage.api.query.filter);
      } catch {
        return null;
      }
    }
    return null;
  }, [storedCurrentPage?.pagination?.filter, storedCurrentPage?.api?.query?.filter]);

  // DataSearch allows both person and company
  const allowedEntityTypes = useMemo(() => {
    return configFilter?.entityType || ['person', 'company'];
  }, [configFilter?.entityType]);

  const entityProfile = useMemo(
    () => configFilter?.entityProfile || ['dataSearch'],
    [configFilter?.entityProfile]
  );

  // Determine the default entityType
  const defaultEntityType = useMemo(() => {
    if (data?.cnpj?.value) return 'company';
    if (data?.cpf?.value) return 'person';
    if (data?.entityType) return data.entityType;
    return 'company';
  }, [data?.entityType, data?.cnpj?.value, data?.cpf?.value]);

  // DataSearch is always hybrid mode (allows both company and person)
  const isHybridMode = true;
  const entityTypeModel = 'manual';

  const tabsItems = useMemo(() => {
    const src = Array.isArray(availableTabs)
      ? availableTabs
      : availableTabs
        ? Object.values(availableTabs)
        : [];
    return src;
  }, [availableTabs]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const initialValues = useMemo(() => buildInitialValues(IS_MOCK_STEP, data), [data]);
  const [disabledFields, setDisabledFields] = useState([]);
  const [state, setState] = useState({
    dataOrigin: 'new_data',
    entityType: defaultEntityType,
    entityProfile,
  });

  // Track phone/email values for phoneEmail OR logic
  const [phoneEmailState, setPhoneEmailState] = useState({
    hasPhone: !!data?.phone?.value,
    hasEmail: !!data?.email?.value,
  });

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS I
  // ─────────────────────────────────────────────────────────────────────────────
  // Update phoneEmailState when data loads
  useEffect(() => {
    setPhoneEmailState({
      hasPhone: !!data?.phone?.value,
      hasEmail: !!data?.email?.value,
    });
  }, [data?.phone?.value, data?.email?.value]);

  // Update state when defaultEntityType changes
  useEffect(() => {
    setState(prev => ({
      ...prev,
      entityType: defaultEntityType,
      entityProfile,
    }));
  }, [defaultEntityType, entityProfile]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleFinish = async ({ values, allValues }) => {
    try {
      const entityId = values?._id || allValues?._id || data?._id;
      const isNewDocument = !_id || _id === 'add';

      const dataToSave = { ...values };

      if (isNewDocument) {
        dataToSave._metadata = { ...dataToSave._metadata, origin: 0 };
      }

      // Include contacts array from data
      let mergedContacts = data?.contacts ? [...data.contacts] : [];

      // Sync phone changes
      const phoneFromForm = allValues?.phone || values?.phone;
      const phoneId = data?.phone?._id;
      const currentPhoneValue = phoneFromForm?.value;
      const hadPhoneBefore = !!data?.phone?.value;

      if (phoneId) {
        if (isEmpty(currentPhoneValue)) {
          mergedContacts = mergedContacts.filter(c => c._id !== phoneId);
          dataToSave.phone = null;
        } else if (currentPhoneValue !== data?.phone?.value) {
          mergedContacts = mergedContacts.map(c =>
            c._id === phoneId ? { ...c, value: currentPhoneValue } : c
          );
        }
      } else if (hadPhoneBefore && isEmpty(currentPhoneValue)) {
        dataToSave.phone = null;
      }

      // Sync email changes
      const emailFromForm = allValues?.email || values?.email;
      const emailId = data?.email?._id;
      const currentEmailValue = emailFromForm?.value;
      const hadEmailBefore = !!data?.email?.value;

      if (emailId) {
        if (isEmpty(currentEmailValue)) {
          mergedContacts = mergedContacts.filter(c => c._id !== emailId);
          dataToSave.email = null;
        } else if (currentEmailValue !== data?.email?.value) {
          mergedContacts = mergedContacts.map(c =>
            c._id === emailId ? { ...c, value: currentEmailValue } : c
          );
        }
      } else if (hadEmailBefore && isEmpty(currentEmailValue)) {
        dataToSave.email = null;
      }

      if (data?.contacts || mergedContacts.length > 0) {
        dataToSave.contacts = mergedContacts;
      }

      // Handle CPF clearing
      const cpfFromForm = allValues?.cpf || values?.cpf;
      const currentCpfValue = cpfFromForm?.value;

      if (isEmpty(currentCpfValue) && data?.cpf?.value) {
        dataToSave.cpf = null;
      }

      // Handle CNPJ clearing
      const cnpjFromForm = allValues?.cnpj || values?.cnpj;
      const currentCnpjValue = cnpjFromForm?.value;

      if (isEmpty(currentCnpjValue) && data?.cnpj?.value) {
        dataToSave.cnpj = null;
      }

      await storedUseApi.upsert({ dataSet: dataToSave, _id: entityId });
    } catch (err) {
      throw err;
    }
  };

  const handleValuesChange = (changed, allValues) => {
    if ('phone' in changed || 'email' in changed) {
      const phoneValue = allValues?.phone?.value;
      const emailValue = allValues?.email?.value;
      setPhoneEmailState({
        hasPhone: !!phoneValue && phoneValue.trim() !== '',
        hasEmail: !!emailValue && emailValue.trim() !== '',
      });
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  // Build requiredFields dynamically
  const requiredFields = useMemo(() => {
    const currentEntityType = state.entityType || defaultEntityType;
    const mandatoryFields = ['countryCode', 'name'];

    if (locale !== 'BR') {
      mandatoryFields.push('taxId', 'address.address1', 'address.region', 'address.city');
    }

    return mandatoryFields;
  }, [state.entityType, defaultEntityType, locale]);

  // Determine initial focus field based on entityType
  const initFocus = useMemo(() => {
    if (locale !== 'BR') return 'name';
    return state.entityType === 'company' ? 'cnpj.value' : 'cpf.value';
  }, [locale, state.entityType]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. EFFECTS II
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const childProps = {
      ...props,
      data,
      initTab,
      state,
      setState,
      viewMode,
      entityTypeModel,
      isHybridMode,
      allowedEntityTypes,
    };
    const itemsLocal = buildTabsArray(childProps);
    buildTabs(itemsLocal);
  }, [state, initTab, viewMode, entityTypeModel, isHybridMode, allowedEntityTypes]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 9. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="container mt-3">
        <SimpleFormProvider
          formRef={formRef}
          onFinish={handleFinish}
          showSubmitButton={false}
          initFocus={initFocus}
          additionalValues={{
            entityProfile: 'dataSearch',
          }}
          reloadEntirePage={true}
          ignoreResetFormOnSubmit={true}
          requiredFields={requiredFields}
          disabledFields={disabledFields}
          locked={viewMode === 'view'}
          showInfoMessage={false}
          initialValues={initialValues}
          data={data}
          onValuesChange={handleValuesChange}
        >
          <SimpleFormProviderInitializer data={data} isInitializedRef={isInitializedRef} />

          <div style={{ position: 'relative' }}>
            <CardHeaderWithCountry
              overlapsBackground
              data={data}
              avatarName={data?.organization}
              watchField="countryCode"
            />

            <SimpleLoadingArea
              name="cardHeader"
              offsetTop={0}
              showProgress={false}
              zIndex={100}
              borderRadius={8}
            />
          </div>

          <Tabs
            type="line"
            tabPlacement="top"
            activeKey={tab}
            rootClassName="tab-in-form no-content-padding"
            items={tabsItems}
            onChange={handleTabChange}
            animated={{ inkBar: true, tabPane: false }}
            destroyOnHidden={false}
          />

          {/*<DocumentIdentifier data={data} />*/}
        </SimpleFormProvider>
      </div>
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default DataSearchDocument;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function buildTabsArray(props) {
  const { initTab, data } = props;

  return [
    {
      key: initTab,
      icon: buildOriginIcon(data),
      label: <span>{translateX('record')}</span>,
      hide: false,
      disabled: false,
      children: <EntityRecordTab {...props} tabName={initTab} />,
    },
    {
      key: 'additionalInfo',
      icon: <FileSyncOutlined />,
      label: <span>{translateX('additional_info')}</span>,
      hide: false,
      disabled: false,
      children: <EntityAdditionalInfoTab />,
    },
  ];
}

function isEmpty(val) {
  return (
    val === null ||
    val === undefined ||
    val === '' ||
    (typeof val === 'string' && val.trim() === '')
  );
}
